import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { clsx } from 'clsx';
import { Landmark, Loader2, Pencil, Plus, Power, PowerOff } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { ConfirmDialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { ActionButton } from '@components/Table';
import { usePermissions } from '@hooks/usePermissions';
import accountService from '@services/accountService';
import { ACCOUNT_TYPE_LABELS, accountToInput, type Account } from '@/types/finance';
import { colorClasses } from '@/utils/colors';
import { AccountFormModal } from './AccountFormModal';
import { AccountStatementDrawer } from './AccountStatementDrawer';
import { BalanceText } from './financeBits';

/**
 * Finanças → Contas: cartões com saldo atual e previsto, total geral, cadastro/edição,
 * inativar/reativar e o extrato em drawer (`?open=<id>`).
 */
export const AccountsView: React.FC = () => {
  const { checkPermission } = usePermissions();
  const canWrite = checkPermission('financas.write').hasPermission;
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState<Account[]>([]);
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState<{ account?: Account } | null>(null);
  const [deactivating, setDeactivating] = useState<Account | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const openId = Number(searchParams.get('open')) > 0 ? Number(searchParams.get('open')) : null;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await accountService.getAll(showInactive));
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (id: number | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('open', String(id));
    else next.delete('open');
    setSearchParams(next, { replace: true });
  };

  const active = useMemo(() => items.filter((a) => a.is_active !== false), [items]);
  const totals = useMemo(
    () => ({
      balance: active.reduce((s, a) => s + a.balance_cents, 0),
      projected: active.reduce((s, a) => s + a.projected_cents, 0),
    }),
    [active],
  );

  const confirmDeactivate = async () => {
    if (!deactivating) return;
    setBusy(deactivating.id);
    try {
      await accountService.deactivate(deactivating.id);
      setDeactivating(null);
      load();
    } catch (err) {
      setDeactivating(null);
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  const reactivate = async (a: Account) => {
    setBusy(a.id);
    try {
      await accountService.update(a.id, accountToInput(a, { is_active: true }));
      load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <Landmark size={20} className="text-slate-500" /> Contas
        </h1>
        <div className="grid grid-cols-1 sm:flex items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input type="checkbox" className="checkbox-field" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />
            Mostrar inativas
          </label>
          <PermissionGuard permission="financas.write">
            <Button variant="primary" size="sm" icon={<Plus />} onClick={() => setForm({})}>
              Nova conta
            </Button>
          </PermissionGuard>
        </div>
      </div>

      {/* Total geral */}
      <div className="card p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Saldo total (hoje)</p>
          <p className="text-xl font-semibold">
            <BalanceText cents={totals.balance} />
          </p>
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Previsto</p>
          <p className="text-xl font-semibold">
            <BalanceText cents={totals.projected} className="italic" />
          </p>
          <p className="text-small">com os lançamentos futuros</p>
        </div>
      </div>

      {loading && items.length === 0 && (
        <p className="text-center text-slate-400 py-6">
          <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
        </p>
      )}
      {!loading && items.length === 0 && (
        <p className="text-center text-slate-500 py-8">Nenhuma conta cadastrada. Comece por "Nova conta" e depois registre o saldo inicial com "Ajustar saldo".</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((a) => {
          const inactive = a.is_active === false;
          return (
            <div
              key={a.id}
              className={clsx('card border-l-4 p-4 space-y-2 cursor-pointer hover:shadow-card-hover transition-shadow', colorClasses(a.color).borderLeft, (loading || inactive) && 'opacity-70')}
              onClick={() => open(a.id)}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-slate-800 truncate">{a.name}</p>
                  <p className="text-small truncate">
                    {ACCOUNT_TYPE_LABELS[a.account_type]}
                    {a.bank_name && ` · ${a.bank_name}`}
                    {a.number && ` · ${a.branch ? `${a.branch}/` : ''}${a.number}`}
                  </p>
                </div>
                {inactive && <span className="badge-neutral">Inativa</span>}
              </div>
              <div className="flex items-end justify-between gap-2">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Saldo atual</p>
                  <p className="text-lg font-semibold">
                    <BalanceText cents={a.balance_cents} />
                  </p>
                  {a.projected_cents !== a.balance_cents && (
                    <p className="text-small">
                      previsto <BalanceText cents={a.projected_cents} className="italic" />
                    </p>
                  )}
                </div>
                {canWrite && (
                  <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                    <ActionButton icon={<Pencil size={16} />} variant="edit" tooltip="Editar" onClick={() => setForm({ account: a })} />
                    {inactive ? (
                      <ActionButton icon={<Power size={16} />} variant="view" tooltip="Reativar" disabled={busy === a.id} onClick={() => reactivate(a)} />
                    ) : (
                      <ActionButton icon={<PowerOff size={16} />} variant="delete" tooltip="Inativar" disabled={busy === a.id} onClick={() => setDeactivating(a)} />
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {form && (
        <AccountFormModal
          account={form.account}
          onClose={() => setForm(null)}
          onSaved={(a) => {
            setForm(null);
            load();
            if (!form.account) open(a.id);
          }}
          onError={setError}
        />
      )}

      {openId && <AccountStatementDrawer accountId={openId} accounts={active} onClose={() => open(null)} onChanged={load} onError={setError} />}

      <ConfirmDialog
        isOpen={!!deactivating}
        onClose={() => setDeactivating(null)}
        onConfirm={confirmDeactivate}
        title="Inativar conta"
        message={`Inativar "${deactivating?.name ?? ''}"? Os lançamentos ficam guardados; a conta some das listas e dos seletores até ser reativada.`}
        confirmText="Inativar"
        severity="warning"
        loading={busy !== null}
      />

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
